/**
 * Document Management Module - Document Upload Page
 * 
 * Form for uploading new documents with comprehensive metadata collection.
 * Supports file upload, categorization, and compliance mapping.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { FaUpload, FaTimes, FaFilePdf, FaFileWord, FaPlus, FaSearch, FaSpinner } from 'react-icons/fa';
import { DocumentCategory, Framework, RiskLevel } from './types';
import documentService from './documentService';
import { getUserInfo } from '../../services/userServicer';
import './DocumentManagement.css';

const DocumentUpload: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Policy' as DocumentCategory,
    framework: '' as Framework | '',
    controlIds: [] as string[],
    riskLevel: '' as RiskLevel | '',
    documentOwner: '',
    documentOwnerName: '',
    expiryDate: '',
    tags: [] as string[],
    folderId: '' as string,
  });
  
  const [tagInput, setTagInput] = useState('');

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Standards/Frameworks state
  const [standards, setStandards] = useState<Array<{ id: string; name: string; code?: string | null; type: string }>>([]);
  const [loadingStandards, setLoadingStandards] = useState(false);

  // Folders state
  const [folders, setFolders] = useState<Array<{ id: string; name: string; path: string; parentId?: string }>>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Controls state with pagination
  const [controls, setControls] = useState<Array<{ id: string; code: string; name: string; description?: string }>>([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [controlsPage, setControlsPage] = useState(1);
  const [controlsTotalPages, setControlsTotalPages] = useState(0);
  const [controlsSearchTerm, setControlsSearchTerm] = useState('');
  const [showControlsModal, setShowControlsModal] = useState(false);
  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(new Set());

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF or DOCX file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return <FaFilePdf className="text-danger" size={48} />;
    } else if (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc')) {
      return <FaFileWord className="text-primary" size={48} />;
    }
    return <FaUpload size={48} />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Fetch standards and user info on component mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch standards
      setLoadingStandards(true);
      try {
        const standardsData = await documentService.getStandards();
        setStandards(standardsData);
      } catch (error: any) {
        toast.error('Failed to load standards: ' + error.message);
      } finally {
        setLoadingStandards(false);
      }

      // Fetch current user info
      try {
        const user = await getUserInfo();
        if (user?.id && user?.name) {
          setFormData(prev => ({
            ...prev,
            documentOwner: user.id,
            documentOwnerName: user.name,
          }));
        }
      } catch (error: any) {
        console.warn('Failed to load user info:', error.message);
        // Don't show error toast for this, as backend will use authenticated user
      }

      // Fetch folders
      setLoadingFolders(true);
      try {
        const foldersData = await documentService.getFolders();
        console.log('Loaded folders:', foldersData); // Debug log
        setFolders(foldersData);
      } catch (error: any) {
        console.error('Failed to load folders:', error);
        toast.error('Failed to load folders: ' + (error.message || 'Unknown error'));
      } finally {
        setLoadingFolders(false);
      }
    };
    fetchData();
  }, []);

  // Fetch controls with pagination
  const fetchControls = async (page: number = 1, search: string = '') => {
    setLoadingControls(true);
    try {
      const result = await documentService.getControls(page, 10, search);
      if (page === 1) {
        setControls(result.data);
      } else {
        setControls(prev => [...prev, ...result.data]);
      }
      setControlsTotalPages(result.totalPages);
      setControlsPage(page);
    } catch (error: any) {
      toast.error('Failed to load controls: ' + error.message);
    } finally {
      setLoadingControls(false);
    }
  };

  // Load initial controls when control modal is opened
  useEffect(() => {
    if (showControlsModal && controls.length === 0) {
      fetchControls(1, controlsSearchTerm);
    }
  }, [showControlsModal]);

  // Handle controls search
  const handleControlsSearch = (searchTerm: string) => {
    setControlsSearchTerm(searchTerm);
    setControlsPage(1);
    setControls([]);
    fetchControls(1, searchTerm);
  };

  // Load more controls (pagination)
  const loadMoreControls = () => {
    if (controlsPage < controlsTotalPages && !loadingControls) {
      fetchControls(controlsPage + 1, controlsSearchTerm);
    }
  };

  // Handle control selection (toggle)
  const handleControlToggle = (control: { id: string; code: string; name: string }) => {
    const controlId = control.code || control.id;
    setSelectedControlIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  };

  // Apply selected controls to form data
  const handleApplyControls = () => {
    setFormData(prev => ({ ...prev, controlIds: Array.from(selectedControlIds) }));
    setShowControlsModal(false);
  };

  // Reset controls when modal closes (cancel)
  const handleModalClose = () => {
    // Reset selected controls to match current form data (discard changes)
    setSelectedControlIds(new Set(formData.controlIds));
    setShowControlsModal(false);
    setControlsSearchTerm('');
    setControlsPage(1);
    setControls([]);
  };

  // Initialize selected controls when modal opens
  useEffect(() => {
    if (showControlsModal) {
      setSelectedControlIds(new Set(formData.controlIds));
    }
  }, [showControlsModal, formData.controlIds]);

  // Remove a control from the list
  const removeControl = (controlId: string) => {
    setFormData(prev => ({
      ...prev,
      controlIds: prev.controlIds.filter(id => id !== controlId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const expiryDate = formData.expiryDate ? new Date(formData.expiryDate) : undefined;
      const tags = formData.tags.length > 0 ? formData.tags : undefined;

      // Build metadata object
      const metadata: any = {
        title: formData.title,
        description: formData.description || undefined,
        category: formData.category,
        framework: formData.framework ? (formData.framework as Framework) : undefined,
        controlId: formData.controlIds.length > 0 ? formData.controlIds.join(', ') : undefined,
        riskLevel: formData.riskLevel ? (formData.riskLevel as RiskLevel) : undefined,
        status: 'Draft', // New documents start as Draft
        expiryDate,
        tags: tags && tags.length > 0 ? tags : undefined,
        folderId: formData.folderId || undefined,
      };

      // Add optional documentOwner fields if they exist
      if (formData.documentOwner) {
        metadata.documentOwner = formData.documentOwner;
      }
      if (formData.documentOwnerName) {
        metadata.documentOwnerName = formData.documentOwnerName;
      }

      const newDocument = await documentService.createDocument(metadata, selectedFile);

      toast.success('Document uploaded successfully!');
      navigate(`/documents/${newDocument.metadata.id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="document-management-container">
      <div className="container-fluid p-4" style={{ paddingTop: '2rem', marginTop: '100px', zoom: 0.9 }}>
            {/* Breadcrumb */}
            <Breadcrumb>
              <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
              <span className="mx-2">/</span>
              <BreadcrumbItem>
                <button
                  className="btn btn-link text-decoration-none p-0"
                  onClick={() => navigate('/documents')}
                  style={{ color: 'inherit' }}
                >
                  Document Management
                </button>
              </BreadcrumbItem>
              <span className="mx-2">/</span>
              <BreadcrumbItem active style={{ paddingRight: '10px' }}>
                Upload New Document
              </BreadcrumbItem>
            </Breadcrumb>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Upload New Document</h5>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Document Information</h5>
                </div>
                <div className="card-body">
                  {/* Title */}
                  <div className="mb-3">
                    <label className="form-label">
                      Document Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., Information Security Policy"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Brief description of the document..."
                    />
                  </div>

                  {/* Category and Framework */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Policy">Policy</option>
                        <option value="Procedure">Procedure</option>
                        <option value="Evidence">Evidence</option>
                        <option value="Regulation">Regulation</option>
                      </select>
                      <small className="form-text text-muted">
                        Type of document in the GRC system
                      </small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Framework / Regulation
                      </label>
                      {loadingStandards ? (
                        <div className="form-select d-flex align-items-center">
                          <FaSpinner className="spinner-border spinner-border-sm me-2" />
                          Loading standards...
                        </div>
                      ) : (
                        <select
                          className="form-select"
                          name="framework"
                          value={formData.framework}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Framework / Regulation</option>
                          {standards.map((standard) => (
                            <option key={standard.id} value={standard.name}>
                              {standard.name} {standard.code ? `(${standard.code})` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <small className="form-text text-muted">
                        Compliance framework this document addresses
                      </small>
                    </div>
                  </div>

                  {/* Folder Selection */}
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Folder (Optional)
                      </label>
                      {loadingFolders ? (
                        <div className="form-control d-flex align-items-center" style={{ minHeight: '38px', pointerEvents: 'none' }}>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading folders...
                        </div>
                      ) : (
                        <select
                          className="form-select"
                          name="folderId"
                          value={formData.folderId}
                          onChange={handleInputChange}
                        >
                          <option value="">No folder (will appear in default structure)</option>
                          {folders.length === 0 ? (
                            <option value="" disabled>No folders available. Create folders in the Tree View.</option>
                          ) : (
                            folders.map((folder) => (
                              <option key={folder.id} value={folder.id}>
                                {folder.path || folder.name}
                              </option>
                            ))
                          )}
                        </select>
                      )}
                      <small className="form-text text-muted">
                        Select a custom folder to organize this document
                      </small>
                    </div>
                  </div>

                  {/* Control ID and Risk Level */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Control IDs</label>
                      <div className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Click to select controls from system"
                          readOnly
                          onClick={() => setShowControlsModal(true)}
                          style={{ cursor: 'pointer' }}
                          value={formData.controlIds.length > 0 ? `${formData.controlIds.length} control(s) selected` : ''}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowControlsModal(true)}
                          title="Select controls from system"
                        >
                          <FaSearch /> Select Controls
                        </button>
                      </div>
                      {formData.controlIds.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {formData.controlIds.map((controlId, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center gap-2"
                              style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                            >
                              {controlId}
                              <button
                                type="button"
                                className="btn-close btn-close-white"
                                style={{ fontSize: '0.7rem' }}
                                aria-label="Remove control"
                                onClick={() => removeControl(controlId)}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Click to select one or more controls from the system
                      </small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Risk Level
                      </label>
                      <select
                        className="form-select"
                        name="riskLevel"
                        value={formData.riskLevel}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Risk Level</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                      </select>
                      <small className="form-text text-muted">
                        Risk level associated with this document
                      </small>
                    </div>
                  </div>

                  {/* Expiry Date and Tags */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleInputChange}
                      />
                      <small className="form-text text-muted">
                        When this document expires and needs review (optional)
                      </small>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Tags</label>
                      <div className="d-flex gap-2 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          placeholder="Enter tag and press Enter or comma"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={addTag}
                        >
                          <FaPlus />
                        </button>
                      </div>
                      {formData.tags.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {formData.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center gap-2"
                              style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                            >
                              {tag}
                              <button
                                type="button"
                                className="btn-close btn-close-white"
                                style={{ fontSize: '0.7rem' }}
                                onClick={() => removeTag(tag)}
                                aria-label="Remove tag"
                              />
                            </span>
                          ))}
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Add tags for easier searching and categorization. Press Enter or comma to add.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">File Upload</h5>
                </div>
                <div className="card-body">
                  {!selectedFile ? (
                    <div
                      className={`file-upload-area ${dragActive ? 'dragover' : ''}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FaUpload size={48} className="text-muted mb-3" />
                      <h5>Drag and drop your file here</h5>
                      <p className="text-muted mb-3">or click to browse</p>
                      <p className="text-muted small">
                        Supported formats: PDF, DOCX (Max size: 10MB)
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        accept=".pdf,.docx,.doc"
                        onChange={handleFileInputChange}
                      />
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                      <div className="d-flex align-items-center gap-3">
                        {getFileIcon(selectedFile.name)}
                        <div>
                          <div className="fw-bold">{selectedFile.name}</div>
                          <div className="text-muted small">{formatFileSize(selectedFile.size)}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={removeFile}
                      >
                        <FaTimes /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/documents')}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-2" />
                      Upload Document
                    </>
                  )}
                </button>
              </div>
            </form>
      </div>

      {/* Control Selection Modal */}
      <Modal isOpen={showControlsModal} toggle={handleModalClose} size="lg">
        <ModalHeader toggle={handleModalClose}>
          <FaSearch className="me-2" />
          Select Controls {selectedControlIds.size > 0 && `(${selectedControlIds.size} selected)`}
        </ModalHeader>
        <ModalBody>
          {/* Search input */}
          <div className="mb-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search controls by code or name..."
                value={controlsSearchTerm}
                onChange={(e) => handleControlsSearch(e.target.value)}
              />
              {loadingControls && (
                <span className="input-group-text">
                  <FaSpinner className="spinner-border spinner-border-sm" />
                </span>
              )}
            </div>
          </div>

          {/* Controls list */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {controls.length === 0 && !loadingControls ? (
              <div className="p-4 text-center text-muted">
                <FaSearch size={48} className="mb-3 opacity-50" />
                <p>No controls found</p>
                {controlsSearchTerm && (
                  <p className="small">Try a different search term</p>
                )}
              </div>
            ) : (
              <>
                {controls.map((control) => {
                  const controlId = control.code || control.id;
                  const isSelected = selectedControlIds.has(controlId);
                  return (
                    <div
                      key={control.id}
                      className="p-3 border-bottom d-flex align-items-start"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleControlToggle(control)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div className="form-check me-3 mt-1" style={{ flexShrink: 0 }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleControlToggle(control)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="fw-bold text-primary">
                          {control.code || control.id}
                        </div>
                        <div className="text-muted mt-1">{control.name}</div>
                        {control.description && (
                          <div className="text-muted small mt-2" style={{ fontSize: '0.875rem' }}>
                            {control.description.length > 150
                              ? `${control.description.substring(0, 150)}...`
                              : control.description}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Load more button */}
                {controlsPage < controlsTotalPages && (
                  <div className="p-3 border-top text-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={loadMoreControls}
                      disabled={loadingControls}
                    >
                      {loadingControls ? (
                        <>
                          <FaSpinner className="spinner-border spinner-border-sm me-2" />
                          Loading...
                        </>
                      ) : (
                        `Load More (Page ${controlsPage} of ${controlsTotalPages})`
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="me-auto">
            <small className="text-muted">
              {selectedControlIds.size} control{selectedControlIds.size !== 1 ? 's' : ''} selected
            </small>
          </div>
          <Button color="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleApplyControls}>
            Apply ({selectedControlIds.size})
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DocumentUpload;

